import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useStoreApi } from '../../api';
import { storeKeys } from '../../query-key-factory';
import { EventDate } from '../../types/eventDate';
import {
  BulkCreateEventDatesRequest,
  BulkCreateEventDatesResponse,
  EventDateRequest,
} from './types';

type CreateEventDateRequest = {
  causeId: string;
  eventId: string;
  eventDate: EventDateRequest;
};

const useCreateEventDate = () => {
  const StoreApi = useStoreApi();
  const queryClient = useQueryClient();

  const createEventDate = async ({ causeId, eventId, eventDate }: CreateEventDateRequest) => {
    const { data } = await StoreApi.post<EventDate>(
      `/store_items/${eventId}/event_dates?cause_id=${causeId}`,
      {
        cause_id: causeId,
        store_item_id: eventId,
        event_date: eventDate,
      }
    );
    return data;
  };

  return useMutation({
    mutationFn: createEventDate,
    onSuccess(_, variables) {
      const { eventId, causeId } = variables;
      void queryClient.invalidateQueries({
        queryKey: storeKeys.id(causeId).items.events.id(eventId).dates.all,
      });
    },
  });
};

const useBulkCreateEventDates = () => {
  const StoreApi = useStoreApi();
  const queryClient = useQueryClient();

  const bulkCreateEventDates = async ({
    causeId,
    eventId,
    eventDates,
    include,
  }: BulkCreateEventDatesRequest) => {
    const queryParams = new URLSearchParams();

    if (causeId) {
      queryParams.append('cause_id', causeId);
    }

    if (include) {
      queryParams.append('include', include);
    }

    const { data } = await StoreApi.post<BulkCreateEventDatesResponse>(
      `/store_items/${eventId}/event_dates/bulk?${queryParams.toString()}`,
      {
        cause_id: causeId,
        store_item_id: eventId,
        event_dates: eventDates,
      }
    );
    return data;
  };

  return useMutation({
    mutationFn: bulkCreateEventDates,
    onSuccess(_, variables) {
      const { eventId, causeId } = variables;
      const key = storeKeys.id(causeId).items.events.id(eventId).dates.all;
      void queryClient.invalidateQueries({
        queryKey: key,
      });
    },
  });
};

export { useBulkCreateEventDates, useCreateEventDate };
